/**
 * keyDisplayPostProcessor — i18next post-processor that swaps translated
 * values for raw key paths when the active URL carries `?i18n=keys`, and
 * highlights only the missing keys when `?i18n=missing` is set.
 *
 * Purpose (issue #140):
 *   - mechanical QA tool for the "no hardcoded English in component JSX"
 *     acceptance criterion — a reviewer hits `?i18n=keys` on the preview
 *     deployment and visually scans for any text that doesn't change
 *   - debug helper for catching keys that resolve to their own path
 *     (i.e. a translation is missing in the active locale and the
 *     i18next fallback chain bottomed out)
 *
 * The processor is per-tab via URL search params — it does NOT persist to
 * storage, so it cannot accidentally leak into the user's normal session.
 * It is registered globally with i18next; the route only resolves the
 * mode on each `t()` call by re-reading `window.location.search`.
 */

import type { PostProcessorModule } from "i18next";

type DebugMode = "off" | "keys" | "missing";

function resolveModeFromUrl(): DebugMode {
	if (typeof window === "undefined" || !window.location) {
		return "off";
	}
	const params = new URLSearchParams(window.location.search);
	const value = params.get("i18n");
	if (value === "keys") return "keys";
	if (value === "missing") return "missing";
	return "off";
}

export const keyDisplayPostProcessor: PostProcessorModule = {
	type: "postProcessor",
	name: "keyDisplay",
	process(value, key /* string | string[] */, _options, _translator) {
		const mode = resolveModeFromUrl();
		if (mode === "off") {
			return value;
		}

		const keyPath = Array.isArray(key) ? key[0] : key;
		if (typeof keyPath !== "string") {
			return value;
		}

		if (mode === "keys") {
			return keyPath;
		}

		// mode === "missing"
		// i18next sets `value === key` when no translation is found at any
		// level of the fallback chain. Highlight that case with a sentinel
		// glyph so missing keys are visually distinct without disturbing
		// resolved ones.
		if (value === keyPath) {
			return `⚠️ ${keyPath}`;
		}
		return value;
	},
};

export default keyDisplayPostProcessor;
